import { c as _c } from "react/compiler-runtime";
import useCartAddToCart from '@/contexts/cartContext/hooks/useCartAddToCart';
import useCartLoading from '@/contexts/cartContext/hooks/useCartLoading';
import useQuery from '@/hooks/useQuery';
import { ActivityAvailableDatesQuery, ActivityAvailableLanguagesQuery, ActivityAvailableTimesQuery } from '@/queries/bookables.gql';
import { safeWarn } from '@/utils/safeLog';
import { ACTIVITY_BOOKING_TAB_KEYS, DEFAULT_PERSONS_MAX, DEFAULT_PERSONS_MIN, DEFAULT_STOCK, INITIAL_PERSONS_FORM_STEP, MINIMAL_ACTIVITY_BOOKING_FILTERS } from '@winelidays/wld-tsx-2/constants';
import { useDateLabel, useExtrasFormStepCreator, useExtrasLabel, useLanguageLabel, usePersonsLabel, useTimeFormStepCreator, useTimeLabel } from '@winelidays/wld-tsx-2/hooks';
import { LFormStepKeyEnum } from '@winelidays/wld-tsx-2/types';
import { arePreviousTabsCompleted, formatDate, isEqual, isValidPositiveInt } from '@winelidays/wld-tsx-2/utils';
import { use, useEffect, useState } from 'react';
import { LANGUAGES, LOCALE } from '@/constants/config';
import { CalculatedPriceQuery } from '@/queries/cart.gql';
import AvailabilityFiltersContext from '../availabilityFiltersContext';
import useCartReservationInProgress from '../cartContext/hooks/useCartReservationInProgress';
import useTranslate from '../i18nContext/hooks/useTranslate';
import ActivityBookingContext from './activityBooking.context';
const getAreDatesStale = ({ loadingCart, lastAvailableDatesQueryVariables, availableDatesVariables, lastAvailableDatesQueryResult }) => {
    if (loadingCart) {
        return false;
    }
    if (!lastAvailableDatesQueryVariables) {
        return true;
    }
    const { dateRange: x, ...limitedVariables } = availableDatesVariables;
    const { dateRange: y, ...limitedQueryVariables } = lastAvailableDatesQueryVariables;
    return !(lastAvailableDatesQueryResult &&
        isEqual(limitedVariables, limitedQueryVariables) && (!x || isEqual(x, y)));
};
const getIsDateValid = ({ availableDates, date }) => {
    if (!date.startDate) {
        return false;
    }
    const formattedDate = formatDate(date.startDate);
    return availableDates.findIndex((d) => d.date === formattedDate) > -1;
};
export const ActivityBookingProvider = (t0) => {
    const $ = _c(121);
    const { children } = t0;
    const translate = useTranslate();
    const addToCart = useCartAddToCart();
    const reservationInProgress = useCartReservationInProgress();
    const loadingCart = useCartLoading();
    const [activeTabKey, setActiveTabKey] = useState(ACTIVITY_BOOKING_TAB_KEYS[0]);
    const [showBookingDetailsModal, setShowBookingDetailsModal] = useState(false);
    const [activityId, setActivityId] = useState(null);
    const [activityStock, setActivityStock] = useState(DEFAULT_STOCK);
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = [];
        $[0] = t1;
    }
    else {
        t1 = $[0];
    }
    const [activityTariffs, setActivityTariffs] = useState(t1);
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[1] = t2;
    }
    else {
        t2 = $[1];
    }
    const [activityExtras, setActivityExtras] = useState(t2);
    const { filters: incomingFilters, isPersonsSet, isDateSet, isLanguageSet, setAdults, setSpecialTariffs, setChildren, setStartDate, setEndDate, setLanguage, resetDateFormStep, resetPersonsFormStep, resetLanguageFormStep, handleResetFilters } = use(AvailabilityFiltersContext);
    const { date, persons, language } = incomingFilters;
    const totalPersons = persons.adults + persons.children.length;
    const personsInput = { adults: persons.adults, children: persons.children, specialTariffs: persons.specialTariffs.map(_temp) };
    const { timeFormStep: time, isTimeSet, setEventTime, resetTimeFormStep } = useTimeFormStepCreator();
    const { extrasFormStep: extras, setExtras, resetExtrasFormStep } = useExtrasFormStepCreator(totalPersons);
    const extrasInput = extras.extras.map(_temp2);
    let t3;
    if ($[2] !== extras || $[3] !== incomingFilters || $[4] !== time) {
        t3 = { ...incomingFilters, time, extras };
        $[2] = extras;
        $[3] = incomingFilters;
        $[4] = time;
        $[5] = t3;
    }
    else {
        t3 = $[5];
    }
    const filters = t3;
    const [personsComplete, setPersonsComplete] = useState(false);
    const [languageComplete, setLanguageComplete] = useState(false);
    const [dateComplete, setDateComplete] = useState(false);
    const [timeComplete, setTimeComplete] = useState(false);
    const [extrasComplete, setExtrasComplete] = useState(false);
    const completion = { persons: personsComplete, language: languageComplete, date: dateComplete, time: timeComplete, extras: extrasComplete };
    const { dateCompletedLabel, dateLabel } = useDateLabel({ translate, locale: LOCALE, isCompleted: completion.date, date });
    const { personsCompletedLabel, personsLabel } = usePersonsLabel({ translate, isCompleted: completion.persons, persons });
    const { languageCompletedLabel, languageLabel } = useLanguageLabel({ translate, languages: LANGUAGES, isCompleted: completion.language, language });
    const { timeCompletedLabel, timeLabel } = useTimeLabel({ translate, isCompleted: completion.time, time });
    const { extrasCompletedLabel, extrasLabel } = useExtrasLabel({ translate, isCompleted: completion.extras, extras });
    let t4;
    if ($[6] !== setAdults || $[7] !== setPersonsComplete) {
        t4 = (adults) => {
            setPersonsComplete(false);
            setAdults(adults);
        };
        $[6] = setAdults;
        $[7] = setPersonsComplete;
        $[8] = t4;
    }
    else {
        t4 = $[8];
    }
    let t5;
    if ($[9] !== setPersonsComplete || $[10] !== setSpecialTariffs) {
        t5 = (specialTariffs) => {
            setPersonsComplete(false);
            setSpecialTariffs(specialTariffs);
        };
        $[9] = setPersonsComplete;
        $[10] = setSpecialTariffs;
        $[11] = t5;
    }
    else {
        t5 = $[11];
    }
    let t6;
    if ($[12] !== setChildren || $[13] !== setPersonsComplete) {
        t6 = (children_0) => {
            setPersonsComplete(false);
            setChildren(children_0);
        };
        $[12] = setChildren;
        $[13] = setPersonsComplete;
        $[14] = t6;
    }
    else {
        t6 = $[14];
    }
    let t7;
    if ($[15] !== activityStock || $[16] !== resetPersonsFormStep || $[17] !== setPersonsComplete) {
        t7 = () => {
            setPersonsComplete(false);
            resetPersonsFormStep(INITIAL_PERSONS_FORM_STEP, activityStock ?? undefined);
        };
        $[15] = activityStock;
        $[16] = resetPersonsFormStep;
        $[17] = setPersonsComplete;
        $[18] = t7;
    }
    else {
        t7 = $[18];
    }
    let t8;
    if ($[19] !== setPersonsComplete) {
        t8 = (t9) => {
            const completed = t9 === undefined ? true : t9;
            setPersonsComplete(completed);
        };
        $[19] = setPersonsComplete;
        $[20] = t8;
    }
    else {
        t8 = $[20];
    }
    let t9;
    if ($[21] !== setLanguage || $[22] !== setLanguageComplete) {
        t9 = (language_0) => {
            setLanguageComplete(false);
            setLanguage(language_0);
        };
        $[21] = setLanguage;
        $[22] = setLanguageComplete;
        $[23] = t9;
    }
    else {
        t9 = $[23];
    }
    let t10;
    if ($[24] !== resetLanguageFormStep || $[25] !== setLanguageComplete) {
        t10 = () => {
            setLanguageComplete(false);
            resetLanguageFormStep();
        };
        $[24] = resetLanguageFormStep;
        $[25] = setLanguageComplete;
        $[26] = t10;
    }
    else {
        t10 = $[26];
    }
    let t11;
    if ($[27] !== setLanguageComplete) {
        t11 = (t12) => {
            const completed_0 = t12 === undefined ? true : t12;
            setLanguageComplete(completed_0);
        };
        $[27] = setLanguageComplete;
        $[28] = t11;
    }
    else {
        t11 = $[28];
    }
    let t12;
    if ($[29] !== setDateComplete || $[30] !== setEndDate || $[31] !== setStartDate) {
        t12 = (date_0) => {
            setDateComplete(false);
            setStartDate(date_0);
            setEndDate(date_0);
        };
        $[29] = setDateComplete;
        $[30] = setEndDate;
        $[31] = setStartDate;
        $[32] = t12;
    }
    else {
        t12 = $[32];
    }
    let t13;
    if ($[33] !== resetDateFormStep || $[34] !== setDateComplete) {
        t13 = () => {
            setDateComplete(false);
            resetDateFormStep();
        };
        $[33] = resetDateFormStep;
        $[34] = setDateComplete;
        $[35] = t13;
    }
    else {
        t13 = $[35];
    }
    let t14;
    if ($[36] !== setDateComplete) {
        t14 = (t15) => {
            const completed_1 = t15 === undefined ? true : t15;
            setDateComplete(completed_1);
        };
        $[36] = setDateComplete;
        $[37] = t14;
    }
    else {
        t14 = $[37];
    }
    let t15;
    if ($[38] !== setEventTime || $[39] !== setTimeComplete) {
        t15 = (eventTime) => {
            setTimeComplete(false);
            setEventTime(eventTime);
        };
        $[38] = setEventTime;
        $[39] = setTimeComplete;
        $[40] = t15;
    }
    else {
        t15 = $[40];
    }
    let t16;
    if ($[41] !== resetTimeFormStep || $[42] !== setTimeComplete) {
        t16 = () => {
            setTimeComplete(false);
            resetTimeFormStep();
        };
        $[41] = resetTimeFormStep;
        $[42] = setTimeComplete;
        $[43] = t16;
    }
    else {
        t16 = $[43];
    }
    let t17;
    if ($[44] !== setTimeComplete) {
        t17 = (t18) => {
            const completed_2 = t18 === undefined ? true : t18;
            setTimeComplete(completed_2);
        };
        $[44] = setTimeComplete;
        $[45] = t17;
    }
    else {
        t17 = $[45];
    }
    let t18;
    if ($[46] !== setExtras) {
        t18 = (extras_0) => {
            setExtras(extras_0);
        };
        $[46] = setExtras;
        $[47] = t18;
    }
    else {
        t18 = $[47];
    }
    let t19;
    if ($[48] !== resetExtrasFormStep || $[49] !== setExtrasComplete) {
        t19 = () => {
            setExtrasComplete(false);
            resetExtrasFormStep();
        };
        $[48] = resetExtrasFormStep;
        $[49] = setExtrasComplete;
        $[50] = t19;
    }
    else {
        t19 = $[50];
    }
    let t20;
    if ($[51] !== setExtrasComplete) {
        t20 = (t21) => {
            const completed_3 = t21 === undefined ? true : t21;
            setExtrasComplete(completed_3);
        };
        $[51] = setExtrasComplete;
        $[52] = t20;
    }
    else {
        t20 = $[52];
    }
    const handlers = { [LFormStepKeyEnum.persons]: { handleSetAdults: t4, handleSetSpecialTariffs: t5, handleSetChildren: t6, handleResetPersons: t7, handleComplete: t8 }, [LFormStepKeyEnum.language]: { handleSetLanguage: t9, handleResetLanguage: t10, handleComplete: t11 }, [LFormStepKeyEnum.date]: { handleSetDate: t12, handleResetDate: t13, handleComplete: t14 }, [LFormStepKeyEnum.time]: { handleSetTime: t15, handleResetTime: t16, handleComplete: t17 }, [LFormStepKeyEnum.extras]: { handleSetExtras: t18, handleResetExtras: t19, handleComplete: t20 } };
    let t21;
    if ($[53] !== handleResetFilters) {
        t21 = () => {
            handleResetFilters();
            setActiveTabKey(ACTIVITY_BOOKING_TAB_KEYS[0]);
        };
        $[53] = handleResetFilters;
        $[54] = t21;
    }
    else {
        t21 = $[54];
    }
    const handleReset = t21;
    const [calculatedPrice, setCalculatedPrice] = useState(null);
    const { data: calculatedPriceData, loading: calculatingPrice } = useQuery(CalculatedPriceQuery, { fetchPolicy: "network-only", skip: !activityId || !completion.persons, variables: { bookableId: activityId, remainingStock: personsInput, locale: LOCALE, extras: extrasInput } });
    let t22;
    if ($[55] !== calculatedPriceData?.calculatedPrice) {
        t22 = () => {
            setCalculatedPrice(calculatedPriceData?.calculatedPrice || null);
        };
        $[55] = calculatedPriceData?.calculatedPrice;
        $[56] = t22;
    }
    else {
        t22 = $[56];
    }
    let t23;
    if ($[57] !== calculatedPriceData) {
        t23 = [calculatedPriceData];
        $[57] = calculatedPriceData;
        $[58] = t23;
    }
    else {
        t23 = $[58];
    }
    useEffect(t22, t23);
    const [fetchLanguages, setFetchLanguages] = useState(false);
    const [lastAvailableLanguagesQueryResult, setLastAvailableLanguagesQueryResult] = useState(null);
    const [lastAvailableLanguagesQueryVariables, setLastAvailableLanguagesQueryVariables] = useState(null);
    const availableLanguagesVariables = { id: activityId, remainingStock: personsInput, locale: LOCALE };
    const areLanguagesStale = !loadingCart && !(lastAvailableLanguagesQueryResult &&
        isEqual(availableLanguagesVariables, lastAvailableLanguagesQueryVariables));
    const skipAvailableLanguages = !fetchLanguages || !activityId || !isPersonsSet || !completion.persons ||
        completion.language && !areLanguagesStale;
    let t24;
    if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
        t24 = () => {
            setLastAvailableLanguagesQueryResult(null);
            setLastAvailableLanguagesQueryVariables(null);
        };
        $[59] = t24;
    }
    else {
        t24 = $[59];
    }
    const { data: availableLanguagesQueryData, loading: availableLanguagesQueryLoading, error: availableLanguagesQueryError, variables: availableLanguagesQueryVariables } = useQuery(ActivityAvailableLanguagesQuery, { fetchPolicy: "network-only", skip: skipAvailableLanguages, variables: availableLanguagesVariables, onError: t24 });
    let t25;
    if ($[60] !== availableLanguagesQueryData?.bookable?.availabilityLanguages || $[61] !== availableLanguagesQueryVariables) {
        t25 = () => {
            const newLanguages = availableLanguagesQueryData?.bookable?.availabilityLanguages;
            if (newLanguages) {
                setLastAvailableLanguagesQueryResult((c) => isEqual(c, newLanguages) ? c : newLanguages);
                setLastAvailableLanguagesQueryVariables((c_0) => isEqual(c_0, availableLanguagesQueryVariables) ?
                    c_0 :
                    availableLanguagesQueryVariables);
            }
        };
        $[60] = availableLanguagesQueryData?.bookable?.availabilityLanguages;
        $[61] = availableLanguagesQueryVariables;
        $[62] = t25;
    }
    else {
        t25 = $[62];
    }
    let t26;
    if ($[63] !== availableLanguagesQueryData || $[64] !== availableLanguagesQueryVariables) {
        t26 = [availableLanguagesQueryData, availableLanguagesQueryVariables];
        $[63] = availableLanguagesQueryData;
        $[64] = availableLanguagesQueryVariables;
        $[65] = t26;
    }
    else {
        t26 = $[65];
    }
    useEffect(t25, t26);
    const loadingAvailableLanguages = availableLanguagesQueryLoading || areLanguagesStale;
    const availableLanguages = areLanguagesStale ? null :
        lastAvailableLanguagesQueryResult;
    const [dateRange, setDateRange] = useState(null);
    const [lastAvailableDatesQueryResult, setLastAvailableDatesQueryResult] = useState(null);
    const [lastAvailableDatesQueryVariables, setLastAvailableDatesQueryVariables] = useState(null);
    const availableDatesVariables = { id: activityId, dateRange, remainingStock: personsInput, lang: language.language, locale: LOCALE };
    const areDatesStale = getAreDatesStale({ loadingCart, lastAvailableDatesQueryVariables, availableDatesVariables, lastAvailableDatesQueryResult });
    const skipAvailableDates = !activityId || !dateRange || !isPersonsSet || !isLanguageSet || !completion.persons || !completion.language ||
        completion.date && !areDatesStale;
    let t27;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
        t27 = () => {
            setLastAvailableDatesQueryResult(null);
            setLastAvailableDatesQueryVariables(null);
        };
        $[66] = t27;
    }
    else {
        t27 = $[66];
    }
    const { data: availableDatesQueryData, loading: availableDatesQueryLoading, error: availableDatesQueryError, variables: availableDatesQueryVariables } = useQuery(ActivityAvailableDatesQuery, { fetchPolicy: "network-only", skip: skipAvailableDates, variables: availableDatesVariables, onError: t27 });
    let t28;
    if ($[67] !== availableDatesQueryData?.bookable?.availabilityDates || $[68] !== availableDatesQueryVariables) {
        t28 = () => {
            const newDates = availableDatesQueryData?.bookable?.availabilityDates;
            if (newDates) {
                setLastAvailableDatesQueryResult((c_1) => isEqual(c_1, newDates) ? c_1 : newDates);
                setLastAvailableDatesQueryVariables((c_2) => isEqual(c_2, availableDatesQueryVariables) ?
                    c_2 :
                    availableDatesQueryVariables);
            }
        };
        $[67] = availableDatesQueryData?.bookable?.availabilityDates;
        $[68] = availableDatesQueryVariables;
        $[69] = t28;
    }
    else {
        t28 = $[69];
    }
    let t29;
    if ($[70] !== availableDatesQueryData || $[71] !== availableDatesQueryVariables) {
        t29 = [availableDatesQueryData, availableDatesQueryVariables];
        $[70] = availableDatesQueryData;
        $[71] = availableDatesQueryVariables;
        $[72] = t29;
    }
    else {
        t29 = $[72];
    }
    useEffect(t28, t29);
    const loadingAvailableDates = availableDatesQueryLoading || areDatesStale;
    const availableDates = areDatesStale ? null : lastAvailableDatesQueryResult;
    const [fetchTimes, setFetchTimes] = useState(false);
    const formattedDate = completion.date && date.startDate ? formatDate(date.startDate) : null;
    const [lastAvailableTimesQueryResult, setLastAvailableTimesQueryResult] = useState(undefined);
    const [lastAvailableTimesQueryVariables, setLastAvailableTimesQueryVariables] = useState(undefined);
    const availableTimesVariables = { id: activityId, date: formattedDate, remainingStock: personsInput, lang: language.language, locale: LOCALE };
    const areTimesStale = !loadingCart && !(lastAvailableTimesQueryResult &&
        isEqual(availableTimesVariables, lastAvailableTimesQueryVariables));
    const skipAvailableTimes = loadingAvailableDates || !fetchTimes || !activityId || !completion.persons || !formattedDate || !completion.language || !completion.date ||
        completion.time && !areTimesStale;
    let t30;
    if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
        t30 = () => {
            setLastAvailableTimesQueryResult(null);
            setLastAvailableTimesQueryVariables(null);
        };
        $[73] = t30;
    }
    else {
        t30 = $[73];
    }
    const { data: availableTimesQueryData, loading: availableTimesQueryLoading, error: availableTimesQueryError, variables: availableTimesQueryVariables } = useQuery(ActivityAvailableTimesQuery, { fetchPolicy: "network-only", skip: skipAvailableTimes, variables: availableTimesVariables, onError: t30 });
    let t31;
    if ($[74] !== availableTimesQueryData?.bookable?.availabilityTimes || $[75] !== availableTimesQueryVariables) {
        t31 = () => {
            const newTimes = availableTimesQueryData?.bookable?.availabilityTimes;
            if (newTimes) {
                setLastAvailableTimesQueryResult((c_3) => isEqual(c_3, newTimes) ? c_3 : newTimes);
                setLastAvailableTimesQueryVariables((c_4) => isEqual(c_4, availableTimesQueryVariables) ?
                    c_4 :
                    availableTimesQueryVariables);
            }
        };
        $[74] = availableTimesQueryData?.bookable?.availabilityTimes;
        $[75] = availableTimesQueryVariables;
        $[76] = t31;
    }
    else {
        t31 = $[76];
    }
    let t32;
    if ($[77] !== availableTimesQueryData || $[78] !== availableTimesQueryVariables) {
        t32 = [availableTimesQueryData, availableTimesQueryVariables];
        $[77] = availableTimesQueryData;
        $[78] = availableTimesQueryVariables;
        $[79] = t32;
    }
    else {
        t32 = $[79];
    }
    useEffect(t31, t32);
    const loadingAvailableTimes = availableTimesQueryLoading || areTimesStale;
    const availableTimes = areTimesStale ? undefined :
        lastAvailableTimesQueryResult;
    let t33;
    if ($[80] !== activityStock || $[81] !== isPersonsSet || $[82] !== totalPersons) {
        t33 = isPersonsSet &&
            isValidPositiveInt(totalPersons) &&
            totalPersons <= (activityStock.upper ?? 0) &&
            totalPersons >= (activityStock.lower ?? 0);
        $[80] = activityStock;
        $[81] = isPersonsSet;
        $[82] = totalPersons;
        $[83] = t33;
    }
    else {
        t33 = $[83];
    }
    const isPersonsValid = t33;
    let t34;
    if ($[84] !== availableLanguages || $[85] !== isLanguageSet || $[86] !== isPersonsValid || $[87] !== language.language || $[88] !== loadingAvailableLanguages) {
        t34 = isLanguageSet &&
            isPersonsValid && !loadingAvailableLanguages &&
            availableLanguages &&
            availableLanguages.findIndex((l) => l === language.language) > -1;
        $[84] = availableLanguages;
        $[85] = isLanguageSet;
        $[86] = isPersonsValid;
        $[87] = language.language;
        $[88] = loadingAvailableLanguages;
        $[89] = t34;
    }
    else {
        t34 = $[89];
    }
    const isLanguageValid = t34;
    let t35;
    if ($[90] !== availableDates || $[91] !== date || $[92] !== isDateSet || $[93] !== loadingAvailableDates) {
        t35 = isDateSet && !loadingAvailableDates && !!availableDates &&
            getIsDateValid({ availableDates, date });
        $[90] = availableDates;
        $[91] = date;
        $[92] = isDateSet;
        $[93] = loadingAvailableDates;
        $[94] = t35;
    }
    else {
        t35 = $[94];
    }
    const isDateValid = t35;
    let t36;
    if ($[95] !== availableTimes || $[96] !== isDateValid || $[97] !== isTimeSet || $[98] !== loadingAvailableTimes || $[99] !== time) {
        t36 = isTimeSet &&
            isDateValid && !loadingAvailableTimes && !!availableTimes &&
            availableTimes.findIndex((t) => isEqual(t.eventTime, time.eventTime)) > -1;
        $[95] = availableTimes;
        $[96] = isDateValid;
        $[97] = isTimeSet;
        $[98] = loadingAvailableTimes;
        $[99] = time;
        $[100] = t36;
    }
    else {
        t36 = $[100];
    }
    const isTimeValid = t36;
    const isLanguageDisabled = !isPersonsValid;
    const isDateDisabled = !completion.persons || !isLanguageValid;
    const isTimeDisabled = !completion.persons || !completion.language || !isDateValid;
    const isExtrasDisabled = !completion.persons || !completion.language || !completion.date || !isTimeValid;
    const tabs = ACTIVITY_BOOKING_TAB_KEYS.map((key, index) => {
        const isCompleted = completion[key];
        let data = null;
        let isValid = false;
        let isDisabled = false;
        let label = "";
        bb350: switch (key) {
            case LFormStepKeyEnum.persons: {
                data = { persons };
                isValid = isPersonsValid;
                isDisabled = false;
                label = personsCompletedLabel ?? personsLabel;
                break bb350;
            }
            case LFormStepKeyEnum.language: {
                data = { language };
                isValid = isLanguageValid;
                isDisabled = isLanguageDisabled;
                label = languageCompletedLabel ?? languageLabel;
                break bb350;
            }
            case LFormStepKeyEnum.date: {
                data = { date };
                isValid = isDateValid;
                isDisabled = isDateDisabled;
                label = dateCompletedLabel ?? dateLabel;
                break bb350;
            }
            case LFormStepKeyEnum.time: {
                data = { time };
                isValid = isTimeValid;
                isDisabled = isTimeDisabled;
                label = timeCompletedLabel ?? timeLabel;
                break bb350;
            }
            case LFormStepKeyEnum.extras: {
                data = { extras };
                isValid = true;
                isDisabled = isExtrasDisabled;
                label = extrasCompletedLabel ?? extrasLabel;
            }
        }
        return { _key: key, _index: index, label, isActive: key === activeTabKey, setActiveTabKey, data, handlers: handlers[key], isCompleted, isValid, isDisabled };
    });
    const firstIncompleteTab = tabs.find(_temp3);
    const activeTab = tabs.find((t_1) => t_1._key === activeTabKey) ?? null;
    useEffect(() => {
        if (completion.persons && (!isPersonsValid || !arePreviousTabsCompleted(LFormStepKeyEnum.persons, tabs))) {
            setPersonsComplete(false);
        }
    }, [
        completion, isPersonsValid, tabs, persons
    ]);
    useEffect(() => {
        if (completion.language && (!isLanguageValid || !arePreviousTabsCompleted(LFormStepKeyEnum.language, tabs))) {
            setLanguageComplete(false);
        }
    }, [
        completion, isLanguageValid, tabs, language
    ]);
    useEffect(() => {
        if (completion.date && (!isDateValid || !arePreviousTabsCompleted(LFormStepKeyEnum.date, tabs))) {
            setDateComplete(false);
        }
    }, [
        completion, isDateValid, tabs, date
    ]);
    useEffect(() => {
        if (completion.time && (!isTimeValid || !arePreviousTabsCompleted(LFormStepKeyEnum.time, tabs))) {
            setTimeComplete(false);
        }
    }, [
        completion, isTimeValid, tabs, time
    ]);
    let t37;
    if ($[101] !== tabs) {
        t37 = tabs.filter(_temp4);
        $[101] = tabs;
        $[102] = t37;
    }
    else {
        t37 = $[102];
    }
    const validCount = t37.length;
    const areFiltersValid = validCount === tabs.length;
    const completeCount = Object.values(completion).filter(_temp5).length;
    const areFiltersCompleted = completeCount === tabs.length;
    const areMinimalFiltersCompleted = MINIMAL_ACTIVITY_BOOKING_FILTERS.reduce((acc, f) => completion[f] ? acc : false, true);
    const filtersInLine = areMinimalFiltersCompleted ?
        `${dateCompletedLabel} - ${personsCompletedLabel} - ${languageCompletedLabel}` :
        `${dateLabel} - ${personsLabel} - ${languageLabel}`;
    let t38;
    if ($[103] !== persons.adults || $[104] !== persons.children || $[105] !== reservationInProgress?.remainingStock || $[106] !== resetExtrasFormStep || $[107] !== resetPersonsFormStep || $[108] !== setDateComplete || $[109] !== setExtrasComplete || $[110] !== setLanguageComplete || $[111] !== setPersonsComplete || $[112] !== setTimeComplete) {
        t38 = (activityId_0, incomingActivityStock, incomingActivityTariffs, incomingActivityExtras) => {
            const newActivityStock = { lower: incomingActivityStock.lower ?? DEFAULT_PERSONS_MIN, upper: incomingActivityStock.upper ?? DEFAULT_PERSONS_MAX };
            const newActivityTariffs = incomingActivityTariffs ?? [];
            const newActivityExtras = incomingActivityExtras ?? [];
            setActivityId(activityId_0);
            setActivityStock(newActivityStock);
            setActivityTariffs(newActivityTariffs);
            setActivityExtras(newActivityExtras);
            const currentPersons = { adults: reservationInProgress?.remainingStock.adults ?? persons.adults ?? DEFAULT_PERSONS_MIN, children: reservationInProgress?.remainingStock.children ?? persons.children ?? [], specialTariffs: [] };
            resetPersonsFormStep(currentPersons, newActivityStock);
            setPersonsComplete(false);
            setLanguageComplete(false);
            setDateComplete(false);
            setTimeComplete(false);
            resetExtrasFormStep();
            setExtrasComplete(false);
        };
        $[103] = persons.adults;
        $[104] = persons.children;
        $[105] = reservationInProgress?.remainingStock;
        $[106] = resetExtrasFormStep;
        $[107] = resetPersonsFormStep;
        $[108] = setDateComplete;
        $[109] = setExtrasComplete;
        $[110] = setLanguageComplete;
        $[111] = setPersonsComplete;
        $[112] = setTimeComplete;
        $[113] = t38;
    }
    else {
        t38 = $[113];
    }
    const handleInitializeFilters = t38;
    const handleBook = () => {
        if (areFiltersValid && date.startDate && time.eventTime) {
            addToCart({ bookableId: activityId, lang: language.language, dateTime: `${formatDate(date.startDate)} ${time.eventTime.time}:00`, event: time.eventTime.event.identifier, eventParent: time.eventTime.event.parent, remainingStock: personsInput, extras: extrasInput });
        }
        else {
            safeWarn("Booking filters are not valid, cannot be added to cart.");
        }
    };
    let t39;
    if ($[114] !== setDateRange || $[115] !== setFetchTimes) {
        t39 = () => {
            setDateRange(null);
            setFetchTimes(false);
        };
        $[114] = setDateRange;
        $[115] = setFetchTimes;
        $[116] = t39;
    }
    else {
        t39 = $[116];
    }
    const handleStopQueries = t39;
    let t40;
    let t41;
    if ($[117] !== activeTab || $[118] !== firstIncompleteTab) {
        t40 = () => {
            if (activeTab && firstIncompleteTab && activeTab._index > firstIncompleteTab._index) {
                setActiveTabKey(firstIncompleteTab._key);
            }
        };
        t41 = [firstIncompleteTab, activeTab];
        $[117] = activeTab;
        $[118] = firstIncompleteTab;
        $[119] = t40;
        $[120] = t41;
    }
    else {
        t40 = $[119];
        t41 = $[120];
    }
    useEffect(t40, t41);
    const contextValue = { activeTab, activeTabKey, setActiveTabKey, stock: activityStock, availableTariffs: activityTariffs, availableDates, availableDatesQueryError, loadingAvailableDates, setDateRange, availableLanguages, availableLanguagesQueryError, loadingAvailableLanguages, setFetchLanguages, availableTimes, availableTimesQueryError, loadingAvailableTimes, setFetchTimes, totalPersons, availableExtras: activityExtras, calculatedPrice, calculatingPrice, filtersInLine, showBookingDetailsModal, setShowBookingDetailsModal, completion, areFiltersCompleted, areFiltersValid, areMinimalFiltersCompleted, completeCount, validCount, handleReset, filters, handleBook, handleInitializeFilters, handleStopQueries, tabs };
    return (<ActivityBookingContext value={contextValue}>{children}</ActivityBookingContext>);
};
function _temp(t0) { const { tariffId, quantity } = t0; return { tariffId, quantity }; }
function _temp2(t0) { const { extraId, quantity: quantity_0 } = t0; return { extraId, quantity: quantity_0 }; }
function _temp3(t_0) { return !t_0.isCompleted; }
function _temp4(tab) { return tab.isValid; }
function _temp5(v) { return v; }
